import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useEffect } from "react";
import { StyleSheet, type ImageSourcePropType } from "react-native";

import {
  logViewJobResumeImprovementsCTAPressed,
  logViewJobResumeImprovementsCTAShown,
} from "../../../../../../interview-prep/logging/cta";
import { Box, Button, Flex, Heading, Image, Text } from "../../../../../../mads";
import { useI18n } from "../../../../../../shared/I18n";
import { JSTLandingScreen } from "../../../../../../shared/native-modules/tare/JSTLandingScreen";
import { logScreenViewWithExtraParams } from "../../../../../../shared/native-modules/tare/logScreenViewWithExtraParams";
import { logTapButtonWithExtraParams } from "../../../../../../shared/native-modules/tare/logTapButtonWithExtraParams";
import navigateTo from "../../../../../../shared/native-modules/tare/navigateTo";

// Same CTA icon MockInterviewCTA uses (RN-7332).
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
const ctaIconBlue = require("../MockInterviewCTA/images/ctaIcon-blue.png") as ImageSourcePropType;

// Card background (RN-7332): the asset bakes in the AI gradient and the swoop
// accent, so the card needs no LinearGradient of its own.
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
const ctaBackground = require("./images/BackgroundColor.png") as ImageSourcePropType;

const screenName = "rn-viewjob-resume-improvements-cta";
const tags = ["interviewPrep", "viewjob"];

/**
 * Resume Improvements entry-point CTA on View Job.
 *
 * After a job seeker completes an AI Mock Interview that generated resume
 * suggestions they haven't acted on, this banner surfaces a "Review
 * suggestions" CTA that deep-links into the resume-improvements review flow.
 * It is omitted when no interview was completed or no suggestions exist.
 *
 * Rendering is controlled by InterviewPrepEntryPoint, which queries
 * getJsInterviewPrepResumeImprovements and only mounts this component when
 * shouldShowCta is true.
 */
interface ResumeImprovementsCTAProps {
  /**
   * The extraction (pipeline) id whose improvements are being reviewed. Logged
   * with the banner's analytics events. Omitted when reviewing the latest
   * extraction.
   */
  extractionId?: string;
}

export const ResumeImprovementsCTA: React.FunctionComponent<ResumeImprovementsCTAProps> = ({
  extractionId,
}) => {
  const i18n = useI18n();

  useEffect(() => {
    void logViewJobResumeImprovementsCTAShown({
      extractionId,
    }).catch(console.error);
    void logScreenViewWithExtraParams(screenName, tags, {
      extractionId: extractionId ?? "",
    }).catch(console.error);
  }, [extractionId]);

  const handleReviewSuggestionsPress = () => {
    void navigateTo(JSTLandingScreen.resumeImprovementsReview(extractionId), "viewjob").catch(
      console.error,
    );

    void logViewJobResumeImprovementsCTAPressed({ extractionId }).catch(console.error);
    void logTapButtonWithExtraParams(screenName, "review_suggestions_button", tags, {
      extractionId: extractionId ?? "",
    }).catch(console.error);
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={styles.backgroundContainer}
        pointerEvents="none"
        accessibilityElementsHidden={true}
        importantForAccessibility="no-hide-descendants"
        testID="resume-improvements-cta-background"
      >
        <Image
          style={styles.backgroundImage}
          source={ctaBackground}
          resizeMode="cover"
          accessible={false}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
        />
      </Box>
      <Box sx={styles.content}>
        <Heading level={4}>{i18n.gettext("Make your resume richer")}</Heading>
        <Text level={2} sx={styles.body}>
          {i18n.gettext(
            "You mentioned a few things in your Mock Interview that aren't on your resume yet. We've made it easy to add them.",
          )}
        </Text>
        <Flex sx={styles.bottomContainer}>
          <Box sx={styles.buttonContainer}>
            <Button variant="secondary" size="md" onPress={handleReviewSuggestionsPress}>
              {i18n.gettext("Review suggestions")}
            </Button>
          </Box>
          <Box sx={styles.illustrationContainer}>
            <Image
              style={styles.illustrationImage}
              source={ctaIconBlue}
              accessible={false}
              accessibilityElementsHidden={true}
              importantForAccessibility="no-hide-descendants"
            />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    margin: spacing.s4,
    borderRadius: borderRadius.md,
    // The background asset is near-white, so on a white screen the card's left
    // edge disappears into the page. A soft drop shadow gives it a visible
    // edge, per spec — same values the viewjob GatedMatchOverview card uses.
    // `backgroundColor` is required for the shadow to render: Android derives
    // the elevation outline from it, and iOS needs an opaque layer to cast
    // from.
    backgroundColor: colors.background,
    shadowColor: colors.utilityShadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: spacing.s2,
    elevation: 4,
  },
  // `overflow: hidden` + a matching `borderRadius` live on this wrapper (rather
  // than the card's outer container) so the background clips to the card's
  // rounded corners without also clipping sibling content that intentionally
  // bleeds past the card edge (e.g. the corner illustration).
  backgroundContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: borderRadius.md,
    overflow: "hidden",
  },
  backgroundImage: {
    width: "100%",
    height: "100%",
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: spacing.s4,
  },
  body: {
    marginTop: spacing.s2,
    color: colors.foregroundStrong,
  },
  bottomContainer: {
    position: "relative",
    marginTop: spacing.s5,
  },
  buttonContainer: {
    maxWidth: 220,
    zIndex: 1,
  },
  illustrationContainer: {
    position: "absolute",
    right: -2,
    bottom: -spacing.s4,
  },
  illustrationImage: {
    width: 107,
    height: 71,
  },
});
